import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchMyAccess } from "./api/authService";
import { accessRefreshed } from "./store/authSlice";

const POLL_MS = 60 * 1000;
const MIN_GAP_MS = 10 * 1000;

/**
 * Keeps the signed-in user's modules and features current. They arrive with the login response, so without this a module an admin
 * switches on or off only shows up after logging out and in again — a page refresh just replays the saved copy. This re-reads them
 * when the app loads, when the tab regains focus, and once a minute while it is open.
 */
export function AccessSync() {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const userId = useAppSelector((state) => state.auth.user?.id);

  useEffect(() => {
    if (!token || !userId) return;
    let last = 0;
    let stopped = false;

    const sync = () => {
      const now = Date.now();
      if (now - last < MIN_GAP_MS || document.visibilityState === "hidden") return;
      last = now;
      fetchMyAccess()
        .then((access) => { if (!stopped) dispatch(accessRefreshed(access)); })
        .catch(() => undefined);
    };

    sync();
    const timer = window.setInterval(sync, POLL_MS);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [dispatch, token, userId]);

  return null;
}
